import { Tracing } from "@tento-core/tracing";
import { DomFlags } from "@tento-ui/dom-flags";
import { $$ } from "@tento-ui/fastdom";
import { FRAME_BUDGET, type FrameBudget, UnitCost } from "@tento-ui/frame-budget";
import { IDLE_LANE, type IdleLane } from "@tento-ui/idle-lane";
import type { Option } from "@tento-lona/sheets";

/**
 * Calendar strip construction, drained under the shared per-frame budget
 * (`@tento-ui/frame-budget`) instead of in whatever frame a strip's loads
 * happened to resolve (F1).
 *
 * A strip bind enqueues one unit per day: read -> spans -> paint for that day,
 * in one write-phase task. The queue runs units in a write-phase task against
 * what the frame has left after the scroll engine's drain, at least one unit
 * per frame (forward progress), and carries the rest to the next frame's
 * write phase. It never raises the budget.
 *
 * - Generation-keyed: each unit says whether it is still wanted (its strip's
 *   bind generation, connected); a superseded unit is dropped at dequeue, and
 *   a strip cancels its units eagerly on reload, recycle and disconnect.
 * - One strip at a time, nearest the viewport first: with a ranker (the
 *   day-column renderer's visible days), each pick takes the strip whose days
 *   lie nearest the viewport (0 = it shows any visible day), a strip already
 *   started before one not started, then the strip queued first; inside that
 *   strip, its day nearest the viewport. A strip reveals only when its last
 *   unit lands, so a started strip's days off the viewport must not wait
 *   behind another strip's visible days (per-day ranking let them). The
 *   ranker is read once per drain, so a strip that scrolled out before it ran
 *   falls behind the visible ones but is never dropped. Without a ranker units run in enqueue order.
 * - Settle-visible: queued units are background debt on the budget, which the
 *   engine counts as pending binds until they drain.
 *
 * Reveal-when-complete stays the strip's: its last unit settles the bind.
 *
 * Idle completion (`calendar-idle-complete`, on by default): a
 * strip whose column parks in the scroll pool (`scroll-column-retention`)
 * registers how to finish its bind (`park`). The queue is a worker on the
 * page's idle lane (`IdleLane`): when the page is still (no
 * input or visible-range move for the lane's quiet interval, no frame work
 * queued anywhere), the lane starts the parked strip nearest
 * the viewport and runs its units (`enqueueIdle`) one at a time within its
 * slice, never in a frame's write phase. Only strips within
 * {@link StripWorkQueue.IDLE_WINDOW_LENGTHS} visible lengths of the viewport
 * run. Idle units are not background debt: a settle never waits for them.
 *
 * One kind of unit: the rough boxes (`calendar-rough-layer`) paint in the
 * strip's bind write, so the queue has no rough unit.
 */

/**
 * Whether strips build under the frame budget (F1), from the `strip-budget`
 * DOM flag (`DomFlags`): on unless `?flag:strip-budget=false` (or localStorage
 * `debug-flag:strip-budget=false`). Off restores the pre-F1 path for an A/B
 * measurement in the same build: every day bound in one task, spans painted
 * after each provider's promise. Read when a renderer is created; the page
 * value is read once, so a stored change applies on reload.
 */
export function stripBudgetEnabled(): boolean {
  return DomFlags.read("strip-budget", true);
}

/**
 * Whether a parked strip finishes its bind in idle time, from the
 * `calendar-idle-complete` DOM flag (`DomFlags`; on by default since the
 * operator's flip of 2026-10-12, beside `scroll-column-retention`). Off, parking drops the strip's units and
 * nothing runs idle.
 */
export function idleCompleteEnabled(): boolean {
  return DomFlags.read("calendar-idle-complete", true);
}

/** One unit of strip work. */
export type StripUnit = {
  /** Whether the unit is still wanted (checked when it is dequeued). */
  readonly current: () => boolean;
  /** Do the work; it runs in a write phase. */
  readonly run: () => void;
  /** The day the unit paints (days since the epoch), for the queue's ranker. */
  readonly day?: number;
};

/** One day of a strip bind, split into steps that each run as their own unit. */
export type StripDay = {
  /** The day (days since the epoch), for ranking. */
  readonly day?: number;
  /** The day's work in order; each step checks the frame budget on its own. */
  readonly steps: readonly (() => void)[];
};

/**
 * One unit per step of every day (a day's steps keep their order: they share
 * its rank and the queue breaks ties in enqueue order). `finish` runs after
 * the last unit to run, whichever day that is, so a bind settles (and the
 * column's stale mark clears) only once every chunk has landed.
 */
export function stripUnits(
  days: readonly StripDay[],
  current: () => boolean,
  finish: () => void,
): StripUnit[] {
  // A day with no steps still takes one (empty) unit, so the bind finishes.
  const stepsOf = (steps: readonly (() => void)[]) => steps.length > 0 ? steps : [() => {}];
  let remaining = 0;
  for (const { steps } of days) remaining += stepsOf(steps).length;
  return days.flatMap(({ day, steps }) =>
    stepsOf(steps).map((step): StripUnit => ({
      current,
      ...(day !== undefined ? { day } : {}),
      run: () => {
        try {
          step();
        } finally {
          if (--remaining === 0) finish();
        }
      },
    }))
  );
}

/** A strip's place in the queue's pick order (`StripWorkQueue.stripRank`): compared element by element, smaller first. */
export type StripRank = readonly [
  distance: number,
  unstarted: number,
  order: number,
];

/** The visible days `[start, end)` (days since the epoch). */
type VisibleDays = { readonly start: number; readonly end: number };

/** Read once per drain: the visible days, or null for enqueue order. */
export type StripRanker = () => Option<VisibleDays>;

/** How the queue reaches the frame loop (fastdom in production, a fake in tests). */
export type StripWorkScheduler = {
  /** Run `task` in this frame's write phase (or the next one outside a flush). */
  readonly thisFrame: (task: () => void) => void;
  /** Run `task` in the next frame's write phase. */
  readonly nextFrame: (task: () => void) => void;
  readonly now: () => number;
  /** The idle lane parked strips finish in (the page's `IDLE_LANE` in production). Without it nothing runs idle. */
  readonly idleLane?: Pick<IdleLane, "register" | "wake">;
};

/** A parked strip waiting to finish its bind in idle time. */
type ParkedStrip = {
  /** The first and last day its bind paints. */
  readonly first: number;
  readonly last: number;
  /** Begin the strip's idle bind (it queues its units with `enqueueIdle`). */
  readonly start: () => void;
};

type QueuedUnit = {
  readonly owner: object;
  readonly unit: StripUnit;
};

/** One owner's (strip's) queued bind, for picking whole strips. */
type QueuedStrip = {
  /** The first and last day its units paint (all its units, run or not). */
  first: number;
  last: number;
  /** When it was first queued (a queue-wide counter). */
  readonly order: number;
  /** Whether any of its units already ran. */
  started: boolean;
};

/**
 * The calendar strips' shared, budgeted work queue (one per day-column
 * renderer); a strip sees it as `CalendarSheetColumn.StripWork`.
 */
export class StripWorkQueue {
  readonly #queue: QueuedUnit[] = [];
  readonly #strips = new Map<object, QueuedStrip>();
  #disposed = false;
  readonly #unregisterIdle: Option<() => void>;
  #order = 0;
  #scheduled = false;
  /** What this queue's units usually cost, for the frame's admission check. */
  readonly #cost = new UnitCost();
  /** Units of idle binds (parked strips), run only in idle periods. */
  readonly #idle: QueuedUnit[] = [];
  /** Parked strips whose idle bind has not started, by owner. */
  readonly #parked = new Map<object, ParkedStrip>();
  /** Idle work done since the last report (`calendar/idle-complete`). */
  #idleStats = { started: 0, units: 0 };
  /** This queue's worker on the idle lane. */
  readonly #idleWorker: IdleLane.Worker = {
    cost: new UnitCost(),
    pending: () => this.#idle.length > 0 || this.#parked.size > 0,
    take: () => this.#takeIdleUnit(),
    periodEnded: (moreLater) => this.#idlePeriodEnded(moreLater),
  };

  constructor(
    readonly budget:
      & Pick<FrameBudget, "mayRunUnit" | "charge" | "addDebt" | "clearDebt">
      & Partial<Pick<FrameBudget, "notePace">>,
    readonly scheduler: StripWorkScheduler,
    readonly ranker: Option<StripRanker>,
  ) {
    this.#unregisterIdle = scheduler.idleLane?.register(this.#idleWorker) ?? null;
  }

  /** Release the renderer's work and document-lane ownership permanently. */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#unregisterIdle?.();
    this.#queue.length = 0;
    this.#idle.length = 0;
    this.#strips.clear();
    this.#parked.clear();
    this.budget.clearDebt(this);
  }

  /** How far from the viewport a parked strip may finish in idle, in visible lengths each side. */
  static readonly IDLE_WINDOW_LENGTHS = 4;

  /** A queue on the document's frame budget, fastdom and idle lane. */
  static forDocument(ranker: Option<StripRanker>): StripWorkQueue {
    return new StripWorkQueue(FRAME_BUDGET, {
      thisFrame: (task) => $$.mutate(task),
      nextFrame: (task) => $$.measureNext(() => $$.mutate(task)),
      now: () => performance.now(),
      idleLane: IDLE_LANE,
    }, ranker);
  }

  /** How many days `day` lies outside the visible days `[start, end)` (0 inside). */
  static viewportDistanceDays(day: number, start: number, end: number): number {
    if (day < start) return start - day;
    if (day >= end) return day - end + 1;
    return 0;
  }

  /** How many units are queued. */
  get size(): number {
    return this.#queue.length;
  }

  /** How many units `owner` has queued. */
  pendingFor(owner: object): number {
    let count = 0;
    for (const entry of this.#queue) if (entry.owner === owner) count++;
    return count;
  }

  /** Queue `units` for `owner`, in order, after every queued unit. */
  enqueue(owner: object, units: readonly StripUnit[]): void {
    if (this.#disposed || units.length === 0) return;
    this.#queue.push(...units.map((unit): QueuedUnit => ({ owner, unit })));
    let strip = this.#strips.get(owner);
    if (strip == null) {
      strip = {
        first: Number.POSITIVE_INFINITY,
        last: Number.NEGATIVE_INFINITY,
        order: this.#order++,
        started: false,
      };
      this.#strips.set(owner, strip);
    }
    for (const { day } of units) {
      if (day == null) continue;
      strip.first = Math.min(strip.first, day);
      strip.last = Math.max(strip.last, day);
    }
    this.budget.addDebt(this);
    this.#schedule(false);
  }

  /** Drop every unit `owner` has queued (frame and idle) and its parked registration; returns how many units. */
  cancel(owner: object): number {
    let removed = 0;
    for (let i = this.#queue.length - 1; i >= 0; i--) {
      if (this.#queue[i]?.owner !== owner) continue;
      this.#queue.splice(i, 1);
      removed++;
    }
    for (let i = this.#idle.length - 1; i >= 0; i--) {
      if (this.#idle[i]?.owner !== owner) continue;
      this.#idle.splice(i, 1);
      removed++;
    }
    this.#parked.delete(owner);
    this.#strips.delete(owner);
    if (this.#queue.length === 0) this.budget.clearDebt(this);
    return removed;
  }

  /** How many idle units `owner` has queued. */
  idlePendingFor(owner: object): number {
    let count = 0;
    for (const entry of this.#idle) if (entry.owner === owner) count++;
    return count;
  }

  /** Whether `owner` is parked waiting for its idle bind to start. */
  isParked(owner: object): boolean {
    return this.#parked.has(owner);
  }

  /**
   * `owner`'s column parked with its bind unfinished (or its read possibly
   * stale): `start` begins its idle bind, which queues with `enqueueIdle`.
   * Replaces an earlier registration of the same owner.
   */
  park(owner: object, days: { readonly first: number; readonly last: number }, start: () => void): void {
    if (this.#disposed || this.scheduler.idleLane == null) return;
    this.#parked.set(owner, { first: days.first, last: days.last, start });
    this.#scheduleIdle();
  }

  /** Queue `units` of `owner`'s idle bind: they run only in idle periods and are not background debt. */
  enqueueIdle(owner: object, units: readonly StripUnit[]): void {
    if (this.#disposed || units.length === 0) return;
    this.#idle.push(...units.map((unit): QueuedUnit => ({ owner, unit })));
    this.#scheduleIdle();
  }

  /** Tell the idle lane there may be idle work. */
  #scheduleIdle(): void {
    if (this.#idle.length > 0 || this.#parked.size > 0) this.scheduler.idleLane?.wake();
  }

  /**
   * The next idle unit (the lane calls it once the page is still): none while
   * frame work is queued here; else the first queued idle unit within the
   * window, else the start of the nearest parked strip within it.
   */
  #takeIdleUnit(): (() => void) | null {
    if (this.#queue.length > 0) return null; // frame work first: the frame drain wakes the lane when it empties
    const visible = this.ranker?.() ?? null;
    const window = visible == null ? Number.POSITIVE_INFINITY
      : StripWorkQueue.IDLE_WINDOW_LENGTHS * Math.max(1, visible.end - visible.start);
    const entry = this.#takeIdle(visible, window);
    if (entry != null) {
      return () => {
        if (!entry.unit.current()) return;
        this.#idleStats.units++;
        entry.unit.run();
      };
    }
    const owner = this.#nearestParked(visible, window);
    if (owner == null) return null;
    const parked = this.#parked.get(owner);
    this.#parked.delete(owner);
    return () => {
      this.#idleStats.started++;
      parked?.start();
    };
  }

  /** An idle period ended: report the idle work since the last report once the window's work ran out. */
  #idlePeriodEnded(moreLater: boolean): void {
    if (moreLater || this.#idleStats.started + this.#idleStats.units === 0) return;
    // Trace hook `calendar/idle-complete`: idle work since the last report, once the window's work ran out.
    Tracing.log("calendar/idle-complete", {
      started: this.#idleStats.started,
      units: this.#idleStats.units,
      parked: this.#parked.size,
      idleQueued: this.#idle.length,
    });
    this.#idleStats = { started: 0, units: 0 };
  }

  /** How far (days) `[first, last]` lies outside `visible` (0 when it shows a visible day, or with no ranker). */
  static #rangeDistance(first: number, last: number, visible: Option<VisibleDays>): number {
    if (visible == null || first > last) return 0;
    if (last < visible.start) return visible.start - last;
    if (first >= visible.end) return first - visible.end + 1;
    return 0;
  }

  /**
   * Remove and return the first queued idle unit within `window` days of
   * `visible`: enqueue order, so a started idle bind finishes before the next
   * parked strip starts (a strip reveals only when whole).
   */
  #takeIdle(visible: Option<VisibleDays>, window: number): QueuedUnit | undefined {
    const index = this.#idle.findIndex((entry) => {
      const day = entry.unit.day;
      return day == null || StripWorkQueue.#rangeDistance(day, day, visible) <= window;
    });
    return index >= 0 ? this.#idle.splice(index, 1)[0] : undefined;
  }

  /** The parked strip nearest `visible` within `window` days, or null. */
  #nearestParked(visible: Option<VisibleDays>, window: number): Option<object> {
    let pick: Option<object> = null;
    let pickDistance = Number.POSITIVE_INFINITY;
    for (const [owner, parked] of this.#parked) {
      const distance = StripWorkQueue.#rangeDistance(parked.first, parked.last, visible);
      if (distance > window || distance >= pickDistance) continue;
      pick = owner;
      pickDistance = distance;
    }
    return pick;
  }

  #schedule(next: boolean): void {
    if (this.#scheduled) return;
    this.#scheduled = true;
    const task = () => this.#drain();
    if (next) this.scheduler.nextFrame(task);
    else this.scheduler.thisFrame(task);
  }

  #drain(): void {
    this.#scheduled = false;
    if (this.#disposed) return;
    const visible = this.ranker?.() ?? null;
    if (this.#queue.length === 0) {
      this.budget.clearDebt(this);
      this.#scheduleIdle();
      return;
    }
    const wanted = this.#queue.length;
    let ran = 0;
    while (this.#queue.length > 0) {
      if (!this.budget.mayRunUnit(ran, this.scheduler.now(), this.#cost)) break;
      const entry = visible == null ? this.#queue.shift() : this.#takeNearest(visible);
      if (entry == null) break;
      if (this.#run(entry)) ran++;
    }
    this.budget.notePace?.("strips", wanted, ran, this.#queue.length);
    if (this.#queue.length > 0) this.#schedule(true);
    else {
      this.budget.clearDebt(this);
      this.#scheduleIdle();
    }
  }

  /** Run a taken unit unless it is superseded; returns whether it ran (and was charged). */
  #run(entry: QueuedUnit): boolean {
    this.#taken(entry.owner);
    if (!entry.unit.current()) return false;
    const start = this.scheduler.now();
    try {
      entry.unit.run();
    } catch (error) {
      Tracing.err("ui")("calendar strip unit failed", { error: String(error) });
    } finally {
      const elapsed = this.scheduler.now() - start;
      this.#cost.note(elapsed);
      this.budget.charge(elapsed);
    }
    return true;
  }

  /** `owner`'s strip lost a unit to a pick: it has started, and is forgotten once nothing of it is queued. */
  #taken(owner: object): void {
    const strip = this.#strips.get(owner);
    if (strip == null) return;
    strip.started = true;
    if (!this.#queue.some((entry) => entry.owner === owner)) this.#strips.delete(owner);
  }

  /** How many days the strip `[first, last]` lies outside `visible` (0 when it shows a visible day). */
  static #stripDistance(strip: QueuedStrip, visible: VisibleDays): number {
    if (strip.first > strip.last) return 0;
    if (strip.last < visible.start) return visible.start - strip.last;
    if (strip.first >= visible.end) return strip.first - visible.end + 1;
    return 0;
  }

  /**
   * A strip's place in the pick order, compared element by element (smaller
   * first; {@link compareStripRanks}):
   * 1. the strip nearest the viewport (days; 0 = it shows one),
   * 2. a started strip before an unstarted one,
   * 3. then the earliest queued.
   */
  static stripRank(strip: {
    /** Whether any of its units ran. */
    readonly state: "started" | "unstarted";
    readonly distance: number;
    readonly order: number;
  }): StripRank {
    const { state, distance, order } = strip;
    return [distance, state === "started" ? 0 : 1, order];
  }

  /** Lexicographic order of two {@link stripRank}s (negative: `a` picks first). */
  static compareStripRanks(a: StripRank, b: StripRank): number {
    for (let i = 0; i < a.length; i++) {
      const d = (a[i] ?? 0) - (b[i] ?? 0);
      if (d !== 0) return d;
    }
    return 0;
  }

  /**
   * Remove and return the next unit: from the strip that ranks first
   * ({@link stripRank}), its day nearest `visible` (enqueue order among
   * equals).
   */
  #takeNearest(visible: VisibleDays): QueuedUnit | undefined {
    let pick: object | null = null;
    let pickRank: Option<StripRank> = null;
    for (const [owner, strip] of this.#strips) {
      const rank = StripWorkQueue.stripRank({
        state: strip.started ? "started" : "unstarted",
        distance: StripWorkQueue.#stripDistance(strip, visible),
        order: strip.order,
      });
      if (pickRank != null && StripWorkQueue.compareStripRanks(rank, pickRank) >= 0) continue;
      pick = owner;
      pickRank = rank;
    }
    let best = -1;
    let bestRank = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.#queue.length; i++) {
      const entry = this.#queue[i];
      if (entry == null || (pick != null && entry.owner !== pick)) continue;
      const day = entry.unit.day;
      const r = day == null ? 0 : StripWorkQueue.viewportDistanceDays(day, visible.start, visible.end);
      if (r < bestRank) {
        best = i;
        bestRank = r;
        if (r <= 0) break;
      }
    }
    if (best >= 0) return this.#queue.splice(best, 1)[0];
    // A strip with nothing queued is stale bookkeeping: forget it and pick again.
    if (pick == null) return undefined;
    this.#strips.delete(pick);
    return this.#takeNearest(visible);
  }
}

